import React, { useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import { DarkTooltip } from "components/Tooltip";

import { ReactComponent as IconCopy } from "assets/images/icon_copy.svg";
import { ReactComponent as IconCheckCopy } from "assets/images/icon_check_copy.svg";

import { isMobile } from "react-device-detect";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

enum CopyStatus {
  Success = "success",
  Error = "error",
}

interface InfoItemProps {
  id: string;
  title: string;
  value: string;
  expandValue?: boolean;
  subtitle?: string;
  secure?: boolean;
  copyEnabled?: boolean;
  copyWithoutSpaces?: boolean;
}

const InfoItem: React.FC<InfoItemProps> = ({
  id,
  title,
  value,
  expandValue,
  subtitle,
  secure = false,
  copyEnabled,
  copyWithoutSpaces,
}) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus | null>(null);

  const onCopy = async () => {
    if (!copyEnabled) return;

    try {
      const valueToCopy = copyWithoutSpaces ? value.replace(/\s+/g, "") : value;
      await navigator.clipboard.writeText(valueToCopy);
      analytics().sendConversionEvent(
        AnalyticsEvents.iban_oplata_copy_info,
        null,
        true,
      );
      analytics().sendConversionEvent(
        `${AnalyticsEvents.iban_oplata_copy_}${id}`,
        null,
        true,
      );
      setCopyStatus(CopyStatus.Success);
      setTimeout(() => {
        setCopyStatus(null);
      }, 1000);
    } catch (err) {
      setCopyStatus(CopyStatus.Error);
    }
  };

  const renderTooltipContent = () => {
    switch (copyStatus) {
      case CopyStatus.Success:
        return (
          <Box display="flex" alignItems="center">
            <IconCheckCopy />
            <Typography variant="subtitle2" color="grey100" ml={1}>
              Скопійоване
            </Typography>
          </Box>
        );
      case CopyStatus.Error:
        return (
          <Typography variant="subtitle2" color="grey100">
            Помилка
          </Typography>
        );
      default:
        return (
          <Typography variant="subtitle2" color="grey100">
            Копіювати
          </Typography>
        );
    }
  };

  return (
    <Box mb={1}>
      <Typography sx={styles.title}>{title}</Typography>
      <Box sx={styles.content}>
        <Box sx={{ overflow: "hidden" }}>
          {expandValue ? (
            <DarkTooltip
              sx={{ display: isMobile ? "none" : "block" }}
              title={
                <Typography variant="subtitle2" color="grey100" ml={1}>
                  {value}
                </Typography>
              }
              arrow
            >
              <Typography
                translate="no"
                variant="body2"
                sx={{
                  ...styles.value,
                  ...(isMobile ? styles.mobileValue : {}),
                }}
                noWrap={!isMobile}
                data-posthog-mask={secure ? "true" : undefined}
                data-testid={id + "-value"}
              >
                {value}
              </Typography>
            </DarkTooltip>
          ) : (
            <Typography
              translate="no"
              variant="body2"
              noWrap
              sx={styles.value}
              data-posthog-mask={secure ? "true" : undefined}
              data-testid={id + "-value"}
            >
              {value}
            </Typography>
          )}
          {subtitle && (
            <Typography sx={{ mt: 0.5 }} variant="subtitle2" color="grey700">
              {subtitle}
            </Typography>
          )}
        </Box>
        <DarkTooltip
          placement="top"
          {...(isMobile ? { open: !!copyStatus } : {})}
          title={renderTooltipContent()}
          arrow
        >
          <IconButton
            disableRipple
            disableTouchRipple
            onClick={onCopy}
            sx={{ padding: 0 }}
            aria-label="copy"
          >
            <IconCopy />
          </IconButton>
        </DarkTooltip>
      </Box>
    </Box>
  );
};

const styles = {
  title: {
    fontFamily: "Inter, sans-serif",
    color: "#707070",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "15.6px",
    marginBottom: "4px",
    textAlign: "left",
    letterSpacing: "0px",
  },
  value: {
    textAlign: "left",
    minHeight: "20px",
    maxWidth: "inherit",
    marginRight: "12px",
  },
  mobileValue: {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflowWrap: "anywhere",
  },
  content: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "start",
  },
};

export default InfoItem;
